import { db } from '@/js/external-js/config/main_config';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';
import { Timestamp, serverTimestamp, deleteField, FieldValue, DocumentData, doc, deleteField, updateDoc } from 'firebase/firestore';

// Interface for Firestore data (with FieldValue for server timestamps)
interface LegalObligationData {
  id: string;
  addedBy: string;
  dateAdded: Date | Timestamp | FieldValue | { toDate: () => Date };
  dateUpdated: Date | Timestamp | FieldValue | { toDate: () => Date };
  owner: string;
  requirement: string;
  updateMethod: string;
  updatedBy: string;
}

// Interface for the processed LegalObligation (with proper Date objects)
export interface LegalObligation extends Omit<LegalObligationData, 'dateAdded' | 'dateUpdated'> {
  dateAdded: Date;
  dateUpdated: Date;
}

/**
 * Fetches all legal obligations from Firestore
 */
export async function getAllLegalObligations(): Promise<LegalObligation[]> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);
    const docSnap = await docRef.get();

    if (!docSnap.exists) {
      console.warn('Legal obligations document does not exist');
      return [];
    }

    const data = docSnap.data() as DocumentData & {
      legalObligations?: Record<string, Omit<LegalObligationData, 'id'>>
    };

    if (!data?.legalObligations) {
      console.warn('No legal obligations data found');
      return [];
    }

    return Object.entries(data.legalObligations).map(([id, obligation]) => {
      const dateAdded = obligation.dateAdded &&
        (obligation.dateAdded as Timestamp)?.toDate
          ? (obligation.dateAdded as Timestamp).toDate()
          : new Date();

      const dateUpdated = obligation.dateUpdated &&
        (obligation.dateUpdated as Timestamp)?.toDate
          ? (obligation.dateUpdated as Timestamp).toDate()
          : new Date();

      return {
        id,
        addedBy: obligation.addedBy || '',
        dateAdded,
        dateUpdated,
        owner: obligation.owner || '',
        requirement: obligation.requirement || '',
        updateMethod: obligation.updateMethod || '',
        updatedBy: obligation.updatedBy || '',
      };
    });
  } catch (error) {
    console.error('Error fetching legal obligations:', error);
    throw new Error('Failed to fetch legal obligations');
  }
}

/**
 * Fetches a single legal obligation by ID
 */
export async function getLegalObligation(id: string): Promise<LegalObligation | null> {
  try {
    const obligations = await getAllLegalObligations();
    return obligations.find(obligation => obligation.id === id) || null;
  } catch (error) {
    console.error(`Error fetching legal obligation ${id}:`, error);
    throw new Error(`Failed to fetch legal obligation ${id}`);
  }
}

/**
 * Creates a new legal obligation in Firestore
 */
export async function createLegalObligation(
  data: Omit<LegalObligationData, 'id' | 'dateAdded' | 'dateUpdated' | 'addedBy' | 'updatedBy'>
): Promise<LegalObligation> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);
    const docSnap = await docRef.get();

    const now = new Date();
    const serverNow = serverTimestamp();
    const newObligation: Omit<LegalObligationData, 'id'> = {
      ...data,
      addedBy: 'system', // This should be replaced with the actual user ID
      updatedBy: 'system', // This should be replaced with the actual user ID
      dateAdded: data.dateAdded ? data.dateAdded : serverNow,
      dateUpdated: serverNow,
    };

    // Create a version with proper dates for the return value
    const returnObligation: Omit<LegalObligation, 'id'> = {
      ...data,
      addedBy: 'system',
      updatedBy: 'system',
      dateAdded: now,
      dateUpdated: now,
    };

    console.debug('[legalObligationsService:createLegalObligation]', data, newObligation);
    const updateData = docSnap.exists
      ? {
        legalObligations: {
          [`${Date.now()}`]: {
            ...newObligation,
            dateUpdated: now,
          },
        }
      }
      : {
        legalObligations: {
          [Date.now()]: newObligation,
        },
      };

    await docRef.set(updateData, { merge: true });

    // Get the generated ID from the update data
    const newId = Object.keys(updateData.legalObligations || {})[0];

    // Return the created obligation with the generated ID and proper dates
    return {
      id: newId,
      ...returnObligation,
    };
  } catch (error) {
    console.error('Error creating legal obligation:', error);
    throw error instanceof Error ? error : new Error('Failed to create legal obligation');
  }
}

/**
 * Updates an existing legal obligation in Firestore
 */
export async function updateLegalObligation(
  id: string,
  updates: Partial<Omit<LegalObligationData, 'id' | 'dateAdded' | 'addedBy'>>
): Promise<LegalObligation> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    const now = new Date();
    const serverNow = serverTimestamp();
    const updateData = {
      legalObligations: {
        [id]: {
          ...updates,
          dateUpdated: serverNow,
          updatedBy: 'system', // This should be replaced with the actual user ID
        },
      },
    };

    await docRef.set(updateData, { merge: true });

    // Return the updated obligation
    const updatedObligation = await getLegalObligation(id);
    if (!updatedObligation) {
      throw new Error('Failed to verify update');
    }
    return updatedObligation;
  } catch (error) {
    console.error(`Error updating legal obligation ${id}:`, error);
    throw error instanceof Error ? error : new Error(`Failed to update legal obligation ${id}`);
  }
}

/**
 * Deletes a legal obligation from Firestore
 */
export async function deleteLegalObligation(id: string): Promise<boolean> {
  try {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docSnap = await db.doc(docPath).get();

    if (!docSnap.exists) {
      return false;
    }

    const data = docSnap.data() as DocumentData & {
      legalObligations?: Record<string, Omit<LegalObligationData, 'id'>>
    };

    if (!data?.legalObligations) {
      return false;
    }

    console.debug('[legalObligationsService:createLegalObligation]', `Removing ID: ${id}`);

    const docRef = doc(db, docPath);
    await updateDoc(docRef, {
      [`legalObligations.${id}`]: deleteField()
    });

    return true;
  } catch (error) {
    console.error(`Error deleting legal obligation ${id}:`, error);
    return false;
  }
}

/**
 * Sets up a real-time listener for legal obligations
 */
export function onLegalObligationsChange(
  callback: (obligations: LegalObligation[]) => void
): () => void {
  let unsubscribe = () => {
    // No-op function
  };

  const setupListener = async () => {
    try {
      const companyID = await waitForCompanyID();
      const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
      const docRef = db.doc(docPath);

      unsubscribe = docRef.onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data()?.legalObligations;
            if (data && typeof data === 'object') {
              const obligations = Object.entries(data).map(([id, obligation]: [string, any]) => ({
                id,
                addedBy: obligation.addedBy || '',
                dateAdded: obligation.dateAdded?.toDate?.() || new Date(),
                dateUpdated: obligation.dateUpdated?.toDate?.() || new Date(),
                owner: obligation.owner || '',
                requirement: obligation.requirement || '',
                updateMethod: obligation.updateMethod || '',
                updatedBy: obligation.updatedBy || '',
              }));
              callback(obligations);
            } else {
              callback([]);
            }
          } else {
            callback([]);
          }
        },
        (error) => {
          console.error('Error in legal obligations listener:', error);
        }
      );
    } catch (error) {
      console.error('Error setting up legal obligations listener:', error);
    }
  };

  setupListener();
  return () => {
    unsubscribe();
  };
}


export default {
  getAllLegalObligations,
  getLegalObligation,
  createLegalObligation,
  updateLegalObligation,
  deleteLegalObligation,
};
